import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import failsaveStorage from './util/failSafeLocalStorage';

let host = undefined;
export const setTimumApiHost = (url) => {
  host = url;
};

export const getTimumApiHost = () => {
  if (host) {
    return host;
  } else {
    return failsaveStorage.get('timumApiHost', 'https://www.timum.de');
  }
};

// we do it this way so that the base url can be determined dynamically
export const getBaseUrl = () => {
  return getTimumApiHost() + '/rest/1';
};

export const constructUrl = (url, props, plain) => {
  if (props.params) {
    for (const [paramName, paramValue] of Object.entries(props.params)) {
      if (Array.isArray(paramValue)) {
        for (let el of paramValue) {
          if (!url.includes('?')) {
            url = `${url}?${paramName}=${el}`;
          } else {
            url = `${url}&${paramName}=${el}`;
          }
        }
      } else {
        if (!url.includes('?')) {
          url = `${url}?${paramName}=${paramValue}`;
        } else {
          url = `${url}&${paramName}=${paramValue}`;
        }
      }
    }
  }

  if (!url.includes('?')) {
    url = `${url}?X-DISABLE-COOKIES=true`;
  } else {
    url = `${url}&X-DISABLE-COOKIES=true`;
  }

  if (plain) {
    return `${getTimumApiHost()}${url}`;
  } else {
    return `${getBaseUrl()}${url}`;
  }
};

export const timumApiSlice = createApi({
  reducerPath: 'timumApi',
  baseQuery: fetchBaseQuery({
    baseUrl: undefined,
    credentials: 'include',
  }),
  tagTypes: [
    'Timeslot',
    'Product',
    'Account',
    'Provider',
    'User',
    'IdentifiedCustomer',
    'BookingConfig',
  ],
  endpoints: (builder) => ({
    // ##########################################
    // # ConsumerAPI v2
    // ##########################################

    cancelableBookable: builder.query({
      query: (props) => ({
        url: constructUrl(`/bookables/cancelable_bookable`, props),
        method: 'get',
        headers: props.headers,
      }),
      providesTags: (/* result = [], error, arg */) => ['Timeslot'],
    }),

    upcomingBookables: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/resources/${props.channelOrResourceId}/upcoming_bookables`,
          props,
        ),
        method: 'post',
        headers: props.headers,
      }),
      providesTags: (/* result = [], error, arg */) => ['Timeslot'],
    }),

    specificBookables: builder.query({
      query: (props) => ({
        url: constructUrl(`/bookables/specific_bookables`, props),
        headers: props.headers,
        method: 'post',
        body: props.body,
      }),
      providesTags: (/* result = [], error, arg */) => ['Timeslot'],
    }),

    activeProducts: builder.query({
      query: (props) => ({
        url: constructUrl(`/products/active_products`, props),
        headers: props.headers,
        method: 'post',
        body: props.body,
      }),
      providesTags: (/* result = [], error, arg */) => ['Product'],
    }),

    specificProducts: builder.query({
      query: (props) => ({
        url: constructUrl(`/products/specific_products`, props),
        headers: props.headers,
        method: 'post',
        body: props.body,
      }),
      providesTags: (/* result = [], error, arg */) => ['Product'],
    }),

    // Public, unauthenticated product-scoped bookingConfig fetch.
    // Customer-facing widget loads this lazily when a product is selected so
    // the product's override slice can be merged on top of the channel config.
    // Default keepUnusedDataFor (60s) is too short for a booking session —
    // 2h covers realistic product-toggle patterns without re-fetching.
    getProductBookingConf: builder.query({
      query: (props) => ({
        url: constructUrl(`/products/${props.prdRef}/bookingConfig`, props),
        headers: props.headers,
        method: 'get',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'BookingConfig', id: `product:${arg.prdRef}` },
      ],
      keepUnusedDataFor: 7200,
    }),

    createAppointmentWithConsumer: builder.mutation({
      query: (props) => ({
        url: constructUrl(
          `/resources/${props.channelOrResourceId}/create_appointment_with_consumer`,
          props,
        ),
        method: 'post',
        body: props.body,
        headers: props.headers,
      }),
      invalidatesTags: (result, error, arg) => {
        const invalidatedTags = [{ type: 'Timeslot' }];

        if (arg.shouldInvalidateCustomerIdentification) {
          invalidatedTags.push({ type: 'IdentifiedCustomer' });
        }

        return invalidatedTags;
      },
    }),

    removeCustomerFromAppointment: builder.mutation({
      query: (props) => ({
        url: constructUrl(
          `/customers/${props.customersId}/appointments/${props.appointmentId}`,
          props,
        ),
        method: 'delete',
        headers: props.headers,
      }),
      invalidatesTags: (/* result, error, arg */) => [{ type: 'Timeslot' }],
    }),

    authenticate: builder.query({
      query: (props) => {
        return {
          url: constructUrl(`/auth/cookieless/login`, props, true),
          headers: props.headers,
          method: 'post',
          body: props.body,
        };
      },
    }),

    /**
     * Returns an object with the following makeup:
     * {
     *  contact: {
     *    name,
     *    email,
     *    mobile,
     *    phone
     *  },
     *  resource: {
     *    name,
     *    description,
     *    msgHelpText
     *  },
     *  provider: {
     *    name
     *  }
     * }
     */
    publicData: builder.query({
      query: (props) => ({
        url: constructUrl(`/resources/public_data`, props),
        headers: props.headers,
        method: 'post',
        body: props.body,
      }),
    }),

    identifyCustomer: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/crms/${props.crmSlug}/resources/${props.channelOrResourceId}/customers/${props.personId}/identify`,
          props,
        ),
        headers: props.headers,
      }),
      providesTags: 'IdentifiedCustomer', //
      keepUnusedDataFor: 9999999, // once identified it's not going to change
    }),

    /**
     * body contain:
     * resource_id*,
     * product_id*,
     * provider_id,
     * timeslotUuid (if available)
     * from* (ISO format),
     * to* (ISO format)
     * -> * marks those which are required.
     */
    reserveAppoinment: builder.mutation({
      query: (props) => ({
        url: constructUrl(
          `/resources/${props.channelOrResourceId}/reserve_appointment`,
          props,
        ),
        method: 'post',
        body: props.body,
        headers: props.headers,
      }),
      invalidatesTags: (/* result, error, arg */) => [{ type: 'Timeslot' }],
    }),

    /**
     * body must contain:
     * placeholder_id (id created for ephemeral customer during call to),
     * appointment_id
     */
    revokeAppointmentReservation: builder.mutation({
      query: (props) => ({
        url: constructUrl(
          `/resources/${props.channelOrResourceId}/revoke_reservation`,
          props,
        ),
        method: 'post',
        body: props.body,
        headers: props.headers,
      }),
      invalidatesTags: (/* result, error, arg */) => [{ type: 'Timeslot' }],
    }),

    // ##########################################
    // # CRM API requests (general api)
    // ##########################################

    getAccount: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/crms/${props.platform}/account/${props.accountReference}`,
          props,
        ),
      }),
      providesTags: (/* result = [], error, arg */) => ['Account'],
    }),

    createAccount: builder.mutation({
      query: (props) => {
        return {
          url: constructUrl(`/crms/${props.platform}/account`, props),
          method: 'post',
          body: JSON.stringify(props.accountData),
        };
      },
      invalidatesTags: (/* result, error, arg */) => [{ type: 'Account' }],
    }),

    getProviders: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/crms/${props.platform}/account/${props.accountReference}/providers`,
          props,
        ),
      }),
      providesTags: (/* result = [], error, arg */) => ['Product'],
    }),

    getProvider: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/crms/${props.platform}/provider/${props.providerReference}`,
          props,
        ),
      }),
    }),

    createProvider: builder.mutation({
      query: (props) => ({
        url: constructUrl(`/crms/${props.platform}/provider`, props),
        method: 'post',
        body: JSON.stringify(
          (() => ({
            user: props.userData ?? {},
            provider: props.providerData ?? {},
            address: props.addressData ?? null,
            account: props.account ?? null,
            sendEmail: props.sendEmail ?? false,
          }))(),
        ),
      }),
      invalidatesTags: (/* result, error, arg */) => [{ type: 'Provider' }],
    }),

    // loginUserViaApi: builder.query({
    //   query: (props) => ({
    //     url: constructUrl(`/crms/${props.platform}/user/loginWithJwt`),
    //   }),
    //   async onQueryStarted(props, { dispatch, queryFulfilled }) {
    //     const { data /* , meta */ } = await queryFulfilled;
    //     if (data) {
    //       dispatch(timumClientAuthorised({ auth2: data.auth2 }));
    //     }
    //   },
    // }),
    getUser: builder.query({
      query: (props) => ({
        url: constructUrl(
          `/crms/${props.platform}/user/${props.userReference}`,
          props,
        ),
      }),
    }),

    createUser: builder.mutation({
      query: (props) => ({
        url: constructUrl(`/crms/${props.platform}/user`, props),
        method: 'post',
        body: JSON.stringify(props.userData),
      }),
      invalidatesTags: (/* result, error, arg */) => [{ type: 'User' }],
    }),
  }),
});

export const {
  // ##########################################
  // # ConsumerAPI v2
  // ##########################################

  useLazyCancelableBookableQuery,
  useCancelableBookableQuery,
  useUpcomingBookablesQuery,
  useLazyUpcomingBookablesQuery,
  useSpecificBookablesQuery,
  useLazySpecificBookablesQuery,
  useActiveProductsQuery,
  useLazyActiveProductsQuery,
  useSpecificProductsQuery,
  useLazySpecificProductsQuery,
  useGetProductBookingConfQuery,
  useLazyGetProductBookingConfQuery,
  useCreateAppointmentWithConsumerMutation,
  useIdentifyCustomerQuery,
  useLazyIdentifyCustomerQuery,
  usePublicDataQuery,
  useLazyPublicDataQuery,
  useReserveAppoinmentMutation,
  useRevokeAppointmentReservationMutation,
  useRemoveCustomerFromAppointmentMutation,
  useAuthenticateQuery,
  useLazyAuthenticateQuery,

  // ##########################################
  // # CRM API requests (general api)
  // ##########################################
  useCreateAccountMutation,
  useGetAccountQuery,
  useLazyGetAccountQuery,
  useGetProvidersQuery,
  useLazyGetProvidersQuery,
  useCreateProviderMutation,
  // useLoginUserViaApiQuery,
  useCreateUserMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
} = timumApiSlice;
